/**
 * SideBarNav Component
 *
 * Description: This component renders a sidebar navigation menu with a list of items.
 * Each item includes a title, an icon, and a URL to navigate to.
 *
 * File Name: SideBarNav.tsx
 * Date: 12-08-2024
 */

import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AppDispatch, RootState } from "@store/store";
import myOrdersIcon from "src/assets/images/sidebar-icon-orders.png";
import myResultIcon from "src/assets/images/sidebar-icon-results.png";
import myHealthIcon from "src/assets/images/sidebar-icon-healthprofile.png";
import searchIcon from "src/assets/images/sidebar-icon-search.png";
import apointmentIcon from "src/assets/images/appointment.png";
import doctorIcon from "src/assets/images/medical-assistance.png";
import myCustomerIcon from "src/assets/images/my-customers.png";
import PrescriptionsIcon from "src/assets/images/prescription-icon.png";
import MyListsIcon from "src/assets/images/my-list-icon.png";
import MySchedulerIcon from "src/assets/images/scheduler-icon.png";
import myItemIcon from "src/assets/images/my-items.png";
import { getIndicators, removeAllIndicators } from "src/Store/redDots";
import Cookies from "js-cookie";
import { customer, doctor, doctorMode } from "src/lib/constans";
import { useDispatch, useSelector } from "react-redux";
import { convertToKebabCase, fetchChatList } from "src/helpers/helperFunctions";
import { ILatestChat } from "src/types/GlobalInterfaces";

interface IListItem {
  isDisplay: boolean;
  title: string;
  icons: string;
  url: string;
  activeUrl: string[];
  count?: any;
  countShown?: boolean;
}

const SideBarNav: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { userDetails, messageUnreadCount } = useSelector(
    (store: RootState) => store.appReducer
  );
  const [mode, setMode] = useState(Cookies.get(doctorMode) || customer);
  const { allIndicators } = useSelector((store: RootState) => store?.redDots);
  const { latestChat } = useSelector((store: RootState) => store.chat);
  const { pathname } = useLocation();
  useEffect(() => {
    dispatch(getIndicators("/customer/user-indicators"));
  }, [pathname, userDetails]);

  // List of navigation items with titles, icons, and URLs
  const listItems: IListItem[] = [
    // Customer-related tabs - commented out
    // { title: "My Orders", icons: myOrdersIcon, url: "/orders", isDisplay: mode === customer, activeUrl: ["/orders"], count: allIndicators?.customerOrder > 0 ? allIndicators?.customerOrder : allIndicators?.customerPharmacyOrder > 0 ? allIndicators?.customerPharmacyOrder : 0 },
    // { title: "My Results", icons: myResultIcon, url: "/results", isDisplay: mode === customer, activeUrl: ["/results"], count: allIndicators?.customerResult > 0 ? allIndicators?.customerResult : 0 },
    // { title: "My Lists", icons: MyListsIcon, url: "/my-lists", isDisplay: mode === customer, activeUrl: ["/my-lists"] },
    // { title: "My Prescriptions", icons: PrescriptionsIcon, url: "/prescriptions", isDisplay: mode === customer, activeUrl: ["/prescriptions"], count: allIndicators?.prescribedPharmacy || allIndicators?.prescribedDiagnostic || allIndicators?.prescribedHomeCare > 0 ? allIndicators?.prescribedHomeCare + allIndicators?.prescribedDiagnostic + allIndicators?.prescribedPharmacy : 0 },
    // { title: "My Health Profile", icons: myHealthIcon, url: "/personal-details", isDisplay: mode === customer, activeUrl: ["/personal-details"] },
    // { title: "My Appointments", icons: apointmentIcon, url: "/appointments", isDisplay: mode === customer, activeUrl: ["/appointments"], count: allIndicators?.customerOnlineAppointment || allIndicators?.customerPhysicalAppointment || allIndicators?.customerHomeCareOrder > 0 ? allIndicators?.customerPhysicalAppointment + allIndicators?.customerHomeCareOrder + allIndicators?.customerOnlineAppointment : 0 },
    // { title: "My Scheduler", icons: MySchedulerIcon, url: "/scheduler", isDisplay: mode === customer, activeUrl: ["/scheduler"] },
    // { title: "My Doctor", icons: doctorIcon, url: "/my-doctors", isDisplay: mode === customer, activeUrl: ["/my-doctors"] },
    // { title: "Find Doctor", icons: searchIcon, url: "/find-doctors", isDisplay: mode === customer, activeUrl: ["/find-doctors"] },

    // Doctor-related tabs - kept active
    {
      title: "My Appointment",
      icons: apointmentIcon,
      url: "/appointments",
      isDisplay: true,
      activeUrl: ["/appointments"],
      count:
        allIndicators?.doctorPhysicalAppointment ||
        allIndicators?.doctorOnlineAppointment > 0
          ? allIndicators?.doctorPhysicalAppointment +
            allIndicators?.doctorOnlineAppointment
          : 0,
    },
    {
      title: "My Customer",
      icons: myCustomerIcon,
      url: "/my-customers",
      isDisplay: true,
      activeUrl: ["/my-customers"],
      count:
        (allIndicators?.shareAccessToDoctor > 0
          ? allIndicators?.shareAccessToDoctor
          : 0) + messageUnreadCount + allIndicators?.customerNotified + allIndicators?.reportNotified,
    },
    {
      title: "My Scheduler",
      icons: MySchedulerIcon,
      url: "/scheduler",
      isDisplay: true,
      activeUrl: ["/scheduler"],
    },
    // // Doctor-related tabs - kept active
    // { title: "My Appointments", icons: apointmentIcon, url: "/appointments", isDisplay: mode === doctor && userDetails?.userRole === doctor, activeUrl: ["/appointments"], count: allIndicators?.doctorPhysicalAppointment || allIndicators?.doctorOnlineAppointment > 0 ? allIndicators?.doctorPhysicalAppointment + allIndicators?.doctorOnlineAppointment : 0 },
    // { title: "My Scheduler", icons: MySchedulerIcon, url: "/scheduler", isDisplay: mode === doctor && userDetails?.userRole === doctor, activeUrl: ["/scheduler"] },
    // { title: "My Customers", icons: myCustomerIcon, url: "/my-customers", isDisplay: mode === doctor && userDetails?.userRole === doctor, activeUrl: ["/my-customers"], count: allIndicators?.shareAccessToDoctor > 0 ? allIndicators?.shareAccessToDoctor : 0 },
  ];
  // Customer-related secondary tabs - commented out
  // const listItemsSec: IListItem[] = [
  //     { title: "Find Doctor", icons: searchIcon, url: "/find-doctors", isDisplay: mode === customer, activeUrl: ["/find-doctors"] },
  // ];

  useEffect(() => {
    // Function to check for cookie changes
    const checkCookieChange = () => {
      const currentMode = Cookies.get(doctorMode) || customer;
      if (currentMode !== mode) {
        setMode(currentMode);
      }
    };

    // Set an interval to check for changes every second (1000ms)
    fetchChatList({ dispatch });
    const intervalId = setInterval(checkCookieChange, 1000);

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  return (
    <div>
      <div className="parent-box customer-sidebar-second-box">
        {/* <div className='my-items-main'>
                    <img src={myItemIcon} alt="" />
                    <span className='font-size-15px theme-color-green font-Poppins-Medium'>My items</span>
                </div> */}
        <ul className="sidebar-nav-items py-2">
          {listItems
            .filter((v) => v.isDisplay)
            .map((item: IListItem, index: number) => (
              <li key={index}>
                <Link
                  id={`sidebar-nav-item-${convertToKebabCase(item?.title)}`}
                  to={item?.url}
                  className={`sidebar-nav-item relative ${
                    item.activeUrl.some((v) => v === pathname)
                      ? "bg-[#eeeeee]"
                      : ""
                  }`}
                >
                  <div className="sidebar-nav-icon">
                    <img
                      src={item?.icons}
                      alt="sidebar-myorders-icon"
                      width={15}
                    />
                  </div>
                  <p className="font-size-14px font-Poppins-SemiBold title-heading">
                    {item?.title || ""}
                  </p>
                  {item?.count ? (
                    item?.countShown ? (
                      <div className="red-dot-show">
                        <p className="font-Poppins-SemiBold text-white font-size-11px">
                          {item.count}
                        </p>
                      </div>
                    ) : (
                      <div className="red-dot-show-only-dot"></div>
                    )
                  ) : null}
                </Link>
              </li>
            ))}
        </ul>
      </div>
      {/* Customer-related secondary tabs - commented out
            {listItemsSec.filter(v => v.isDisplay).map((item: IListItem, index: number) => (
                <div key={index} className='parent-box customer-sidebar-second-box'>
                    <ul className='sidebar-nav-items py-2'>
                        <li>
                            <Link to={item?.url} className={`sidebar-nav-item ${item.activeUrl.some(v => v === pathname) ? "bg-[#eeeeee]" : ""}`}>
                                <div className="sidebar-nav-icon">
                                    <img src={item?.icons} alt="sidebar-myorders-icon" width={15} />
                                </div>
                                <p className='font-size-14px font-Poppins-SemiBold title-heading'>{item?.title || ""}</p>
                            </Link>
                        </li>
                    </ul>
                </div>
            ))}
            */}
    </div>
  );
};

export default SideBarNav;
